import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  IconButton,
  Tooltip,
  Avatar,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Euro as EuroIcon,
  AttachMoney as DollarIcon
} from '@mui/icons-material';
import api from '@/services/authService';

interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  balances?: Array<{ currency: string; balance: number }>;
}

interface BalanceAdjustment {
  id: number;
  userId: number;
  adminId: number;
  amount: number | string;
  currency: 'EUR' | 'USD';
  adjustmentType: 'add' | 'deduct';
  reason: string;
  previousBalance: number | string;
  newBalance: number | string;
  createdAt: string;
  user?: User;
  admin?: User;
}

const BalanceAdjustment: React.FC = () => {
  // Sample data for testing when API returns empty
  const sampleAdjustments: BalanceAdjustment[] = [
    {
      id: 1,
      userId: 2,
      adminId: 1,
      amount: "100.00",
      currency: "USD",
      adjustmentType: "add",
      reason: "Initial balance setup",
      previousBalance: "0.00",
      newBalance: "100.00",
      createdAt: "2026-03-22 15:30:00",
      user: {
        id: 2,
        username: "testuser",
        email: "testuser@example.com",
        fullName: "Test User"
      },
      admin: {
        id: 1,
        username: "admin",
        email: "admin@example.com",
        fullName: "Admin User"
      }
    },
    {
      id: 2,
      userId: 2,
      adminId: 1,
      amount: "50.00",
      currency: "EUR",
      adjustmentType: "deduct",
      reason: "Test withdrawal",
      previousBalance: "100.00",
      newBalance: "50.00",
      createdAt: "2026-03-22 16:00:00",
      user: {
        id: 2,
        username: "testuser",
        email: "testuser@example.com",
        fullName: "Test User"
      },
      admin: {
        id: 1,
        username: "admin",
        email: "admin@example.com",
        fullName: "Admin User"
      }
    }
  ];
  
  const [adjustments, setAdjustments] = useState<BalanceAdjustment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<'EUR' | 'USD'>('EUR');
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'deduct'>('add');
  const [reason, setReason] = useState('');
  
  // Filter state
  const [filterUserId, setFilterUserId] = useState<string>('');

  const fetchAdjustments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page, limit };
      if (filterUserId) {
        params.userId = filterUserId;
      }
      
      const response = await api.get('/admin/balance-adjustments', { params });
      
      console.log('Full API response:', response);
      console.log('Response data:', response.data);
      console.log('Response data.data:', response.data.data);
      console.log('Adjustments array:', response.data.data?.adjustments);
      console.log('Adjustments length:', response.data.data?.adjustments?.length);
      
      // API returns { success: true, data: { adjustments: [...], pagination: {...} } }
      const adjustmentsData = response.data.data?.adjustments || [];
      const paginationData = response.data.data?.pagination || { pages: 1 };
      
      console.log('Parsed adjustments:', adjustmentsData);
      console.log('Parsed pagination:', paginationData);
      
      setAdjustments(adjustmentsData);
      setTotalPages(paginationData.pages);
    } catch (err: any) {
      console.error('Error fetching adjustments:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch balance adjustments');
    } finally {
      setLoading(false);
    }
  }, [page, filterUserId]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/users', { params: { limit: 1000 } });
      setUsers(response.data.data.users);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  useEffect(() => {
    fetchAdjustments();
    fetchUsers();
  }, [fetchAdjustments, fetchUsers]);

  const handleAdjustmentSubmit = async () => {
    if (!selectedUser || !amount || !reason) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(
        `/admin/users/${selectedUser}/balance-adjustment`,
        {
          amount: parseFloat(amount),
          currency,
          adjustmentType,
          reason
        }
      );

      setSuccess('Balance adjusted successfully');
      setDialogOpen(false);
      setSelectedUser('');
      setAmount('');
      setReason('');
      fetchAdjustments();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to adjust balance');
    } finally {
      setLoading(false);
    }
  };

  const getUserBalance = (userId: number, currency: string): number => {
    const user = users.find(u => u.id === userId);
    if (!user || !user.balances) return 0;
    const balance = user.balances.find(b => b.currency === currency);
    return balance ? balance.balance : 0;
  };

  const getCurrencySymbol = (currency: string): string => {
    return currency === 'EUR' ? '€' : '$';
  };

  const formatCurrency = (amount: number | string | null | undefined, currency: string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
    const validCurrency = (currency && currency.length === 3) ? currency.toUpperCase() : 'USD';
    
    if (isNaN(numAmount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: validCurrency
      }).format(0);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: validCurrency
    }).format(numAmount);
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by User</InputLabel>
            <Select
              value={filterUserId}
              label="Filter by User"
              onChange={(e) => {
                setFilterUserId(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Users</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id.toString()}>
                  {user.fullName} ({user.username})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAdjustments}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          New Balance Adjustment
        </Button>
      </Box>

      {/* Balance Adjustments Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Balance Adjustment History
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !adjustments || adjustments.length === 0 ? (
            <Alert severity="info">No balance adjustments found</Alert>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Admin</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Previous Balance</TableCell>
                      <TableCell>New Balance</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(adjustments) && adjustments.length > 0 && adjustments.map((adjustment) => (
                      <TableRow key={adjustment.id} hover>
                        <TableCell>{formatDate(adjustment.createdAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2">
                                {adjustment.user?.fullName || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                @{adjustment.user?.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
                              <AdminIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2">
                              {adjustment.admin?.fullName || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={adjustment.adjustmentType === 'add' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            label={adjustment.adjustmentType === 'add' ? 'Add' : 'Deduct'}
                            color={adjustment.adjustmentType === 'add' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={adjustment.adjustmentType === 'add' ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {adjustment.adjustmentType === 'add' ? '+' : '-'}
                            {formatCurrency(adjustment.amount, adjustment.currency || 'USD')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(adjustment.previousBalance ?? 0, adjustment.currency || 'USD')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(adjustment.newBalance ?? 0, adjustment.currency || 'USD')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {adjustment.reason || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* New Adjustment Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {adjustmentType === 'add' ? <AddIcon /> : <RemoveIcon />}
            {adjustmentType === 'add' ? 'Add Funds' : 'Deduct Funds'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>User *</InputLabel>
                <Select
                  value={selectedUser}
                  label="User *"
                  onChange={(e) => setSelectedUser(Number(e.target.value) || '')}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Box>
                        <Typography variant="body2">{user.fullName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          @{user.username} - {user.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Adjustment Type</InputLabel>
                <Select
                  value={adjustmentType}
                  label="Adjustment Type"
                  onChange={(e) => setAdjustmentType(e.target.value as 'add' | 'deduct')}
                >
                  <MenuItem value="add">Add Funds</MenuItem>
                  <MenuItem value="deduct">Deduct Funds</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={currency}
                  label="Currency"
                  onChange={(e) => setCurrency(e.target.value as 'EUR' | 'USD')}
                >
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {currency === 'EUR' ? <EuroIcon fontSize="small" /> : <DollarIcon fontSize="small" />}
                        <Typography variant="body2" color="textSecondary">
                          {currency}
                        </Typography>
                      </Box>
                    </InputAdornment>
                  ),
                  inputProps: { min: 0.01, step: 0.01 }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason *"
                multiline
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for this balance adjustment..."
              />
            </Grid>

            {selectedUser && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {currency === 'EUR' ? <EuroIcon fontSize="small" /> : <DollarIcon fontSize="small" />}
                    <Typography variant="body2">
                      Current {currency} balance: <strong>{formatCurrency(getUserBalance(Number(selectedUser), currency), currency)}</strong>
                    </Typography>
                  </Box>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAdjustmentSubmit}
            variant="contained"
            disabled={loading}
            startIcon={adjustmentType === 'add' ? <AddIcon /> : <RemoveIcon />}
          >
            {loading ? 'Processing...' : adjustmentType === 'add' ? 'Add Funds' : 'Deduct Funds'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BalanceAdjustment;
